import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Settings, FileText, Layers, ListTree, FolderKanban, HelpCircle,
  Star, Building2, Award, Newspaper, Image as ImageIcon, Inbox,
  History, LogOut, Save, Upload, Loader2, ShieldCheck, KeyRound,
  Mail, BarChart3, Users,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import EmailSettings from "@/admin/EmailSettings";
import ResourceEditor, { ResourceListWithEdit } from "@/admin/ResourceEditor";
import PageEditor from "@/admin/PageEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const ax = axios.create({ baseURL: API, withCredentials: true });

// ---------- auth context ----------
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [me, setMe] = useState(undefined);
  const [token, setToken] = useState(() => localStorage.getItem("img_admin_token") || "");

  useEffect(() => {
    if (token) ax.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete ax.defaults.headers.common.Authorization;
  }, [token]);

  useEffect(() => {
    if (!token) { setMe(null); return; }
    ax.get("/me").then(({ data }) => setMe(data)).catch(() => { setMe(null); setToken(""); localStorage.removeItem("img_admin_token"); });
  }, [token]);

  const login = async (email, password) => {
    const { data } = await ax.post("/login", { email, password });
    localStorage.setItem("img_admin_token", data.token);
    setToken(data.token);
    setMe(data.admin);
  };
  const logout = () => {
    try { ax.post("/logout"); } catch (e) { /* noop */ }
    localStorage.removeItem("img_admin_token");
    setToken(""); setMe(null);
  };

  return <AuthCtx.Provider value={{ me, token, login, logout }}>{children}</AuthCtx.Provider>;
}

function ProtectedRoute({ children }) {
  const { me } = useAuth();
  if (me === undefined) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;
  if (!me) return <Navigate to="/admin/login" replace />;
  return children;
}

// ---------- helpers ----------
const inputCls = "w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-navy-800 placeholder:text-navy-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20";

function fmtError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Request failed";
}

// ---------- pages ----------
function AdminLogin() {
  const { login, me } = useAuth();
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  // Honor ?next= so callers like DcApp can return the user to /dc after login.
  // Only accept same-origin absolute paths (must start with a single "/" but not "//")
  // to avoid open-redirect risk.
  const nextParam = new URLSearchParams(loc.search).get("next") || "";
  const safeNext = /^\/[^/]/.test(nextParam) ? nextParam : "/admin";

  useEffect(() => {
    axios.get(`${API}/setup-status`).then(({ data }) => setSetupNeeded(data.needs_setup));
  }, []);
  useEffect(() => { if (me) nav(safeNext, { replace: true }); }, [me, nav, safeNext]);

  if (setupNeeded) return <Navigate to="/admin/setup" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, pw); toast.success("Welcome back"); nav(safeNext, { replace: true }); }
    catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-soft-gray flex items-center justify-center p-6">
      <Toaster richColors position="top-center" />
      <form onSubmit={onSubmit} data-testid="admin-login-form" className="w-full max-w-md rounded-3xl bg-white shadow-card border border-navy-50 p-10">
        <div className="flex justify-center"><Logo variant="navy" /></div>
        <h1 className="mt-7 font-display font-bold text-2xl text-navy-800 text-center">Admin sign-in</h1>
        <p className="mt-2 text-sm text-navy-700/70 text-center">Authorised personnel only.</p>
        <div className="mt-7 space-y-4">
          <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@infinitymg.so" className={inputCls} />
          <input data-testid="admin-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className={inputCls} />
          <button data-testid="admin-login-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Sign in
          </button>
        </div>
      </form>
    </div>
  );
}

function AdminSetup() {
  const [form, setForm] = useState({ email: "", password: "", full_name: "" });
  const [busy, setBusy] = useState(false);
  const [needed, setNeeded] = useState(true);
  const nav = useNavigate();
  useEffect(() => { axios.get(`${API}/setup-status`).then(({ data }) => setNeeded(data.needs_setup)); }, []);
  if (!needed) return <Navigate to="/admin/login" replace />;
  const u = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/setup`, form); toast.success("Admin created. Please sign in."); nav("/admin/login"); }
    catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-soft-gray flex items-center justify-center p-6">
      <Toaster richColors position="top-center" />
      <form onSubmit={onSubmit} data-testid="admin-setup-form" className="w-full max-w-md rounded-3xl bg-white shadow-card border border-navy-50 p-10">
        <div className="flex justify-center"><Logo variant="navy" /></div>
        <h1 className="mt-7 font-display font-bold text-2xl text-navy-800 text-center">First-run admin setup</h1>
        <p className="mt-2 text-sm text-navy-700/70 text-center">Create the first administrator account.</p>
        <div className="mt-7 space-y-4">
          <input data-testid="setup-name" required minLength={2} value={form.full_name} onChange={u("full_name")} placeholder="Full name" className={inputCls} />
          <input data-testid="setup-email" required type="email" value={form.email} onChange={u("email")} placeholder="Email" className={inputCls} />
          <input data-testid="setup-password" required minLength={8} type="password" value={form.password} onChange={u("password")} placeholder="Password (min 8 chars)" className={inputCls} />
          <button data-testid="setup-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60">
            <ShieldCheck className="h-4 w-4" /> Create admin
          </button>
        </div>
      </form>
    </div>
  );
}

const MODULES = [
  { slug: "site-settings", label: "Site Settings", icon: Settings },
  { slug: "pages", label: "Pages", icon: FileText },
  { slug: "divisions", label: "Divisions", icon: Layers },
  { slug: "services", label: "Services", icon: ListTree },
  { slug: "team-members", label: "Team", icon: Users },
  { slug: "projects", label: "Projects", icon: FolderKanban },
  { slug: "faqs", label: "FAQs", icon: HelpCircle },
  { slug: "testimonials", label: "Testimonials", icon: Star },
  { slug: "partners", label: "Partners", icon: Building2 },
  { slug: "certifications", label: "Certifications", icon: Award },
  { slug: "posts", label: "News / Posts", icon: Newspaper },
  { slug: "media", label: "Media Library", icon: ImageIcon },
  { slug: "inbox", label: "Contact Inbox", icon: Inbox },
  { slug: "email-settings", label: "Email Settings", icon: Mail },
  { slug: "audit", label: "Audit Log", icon: History },
];

function AdminShell({ children }) {
  const { me, logout } = useAuth();
  const loc = useLocation();
  const active = MODULES.find(m => loc.pathname.includes(`/admin/${m.slug}`));
  return (
    <div className="min-h-screen bg-soft-gray flex">
      <Toaster richColors position="top-center" />
      <aside className="hidden md:flex flex-col w-64 bg-navy text-white p-5">
        <Link to="/admin"><Logo variant="white" /></Link>
        <nav className="mt-10 flex-1 flex flex-col gap-1">
          <Link to="/admin" data-testid="nav-dashboard" className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${loc.pathname === "/admin" ? "bg-white/10 text-teal-light" : "text-white/80 hover:bg-white/5"}`}>
            <BarChart3 className="h-4 w-4" /> Dashboard
          </Link>
          {MODULES.map(m => {
            const Icon = m.icon;
            const isActive = active?.slug === m.slug;
            return (
              <Link key={m.slug} to={`/admin/${m.slug}`} data-testid={`nav-${m.slug}`} className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-teal-light" : "text-white/80 hover:bg-white/5"}`}>
                <Icon className="h-4 w-4" /> {m.label}
              </Link>
            );
          })}
        </nav>
        <div className="mt-6 pt-4 border-t border-white/10 text-xs text-white/65">
          <p className="font-semibold text-white">{me?.full_name || me?.email}</p>
          <p className="truncate">{me?.email}</p>
          <button data-testid="logout-btn" onClick={logout} className="mt-3 inline-flex items-center gap-1.5 hover:text-teal-light"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 p-8 overflow-x-hidden">{children}</main>
    </div>
  );
}

function Dashboard() {
  const [counts, setCounts] = useState({});
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    ax.get("/_crud/services").then(({ data }) => setCounts(c => ({ ...c, services: data.length })));
    ax.get("/_crud/projects").then(({ data }) => setCounts(c => ({ ...c, projects: data.length })));
    ax.get("/contact-messages").then(({ data }) => setCounts(c => ({ ...c, messages: data.total })));
    ax.get("/analytics/quote-requests?window=30d").then(({ data }) => setAnalytics(data));
  }, []);
  return (
    <>
      <h1 className="font-display font-bold text-3xl text-navy-800">Dashboard</h1>
      <p className="mt-2 text-navy-700/70">Manage content, media, and contact messages.</p>
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {[
          { label: "Services", value: counts.services ?? "—" },
          { label: "Projects", value: counts.projects ?? "—" },
          { label: "Contact messages", value: counts.messages ?? "—" },
        ].map(s => (
          <div key={s.label} className="p-7 rounded-2xl bg-white border border-navy-50">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-navy-500">{s.label}</p>
            <p className="mt-3 font-display font-bold text-3xl text-navy-800">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 p-7 rounded-2xl bg-white border border-navy-50" data-testid="analytics-widget">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold text-teal">Quote requests · last 30 days</p>
              <p className="mt-2 font-display font-bold text-2xl text-navy-800">{analytics?.total ?? "—"} requests</p>
            </div>
            <p className="text-xs text-navy-500">avg {analytics?.avg_per_day ?? 0}/day</p>
          </div>
          <div className="mt-6 space-y-2">
            {(analytics?.by_service || []).slice(0, 6).map((s) => {
              const max = Math.max(...analytics.by_service.map(x => x.count));
              const pct = max ? (s.count / max) * 100 : 0;
              return (
                <Link
                  key={s.service_slug}
                  to={`/admin/inbox?source=quote_request&service=${s.service_slug}`}
                  className="block hover:bg-soft-gray rounded-xl p-2 -mx-2"
                  data-testid={`analytics-bar-${s.service_slug}`}
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-navy-700 font-medium truncate">{s.service_name}</span>
                    <span className="text-navy-500 font-semibold">{s.count}</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-navy-50 overflow-hidden">
                    <div className="h-full bg-teal" style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              );
            })}
            {(analytics?.by_service || []).length === 0 && (
              <p className="text-sm text-navy-500">No quote requests in this window yet.</p>
            )}
          </div>
        </div>
        <div className="p-7 rounded-2xl bg-navy text-white" data-testid="backup-card">
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-teal-light">Backups</p>
          <p className="mt-2 font-display font-bold text-xl">Download a full snapshot.</p>
          <p className="mt-3 text-sm text-white/70">JSON export of all collections plus media manifest with SHA-256 hashes.</p>
          <button
            data-testid="backup-download"
            onClick={async () => {
              try {
                const resp = await ax.get("/backup", { responseType: "blob" });
                const blob = new Blob([resp.data], { type: "application/json" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `img-backup-${new Date().toISOString().slice(0, 10)}.json`;
                a.click();
                toast.success("Backup downloaded");
              } catch (err) { toast.error(fmtError(err)); }
            }}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors"
          >
            <Save className="h-4 w-4" /> Download backup
          </button>
        </div>
      </div>
    </>
  );
}

function SiteSettingsEditor() {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ax.get("/site-settings/_").then(({ data }) => setDoc(data)); }, []);
  if (!doc) return <Loader2 className="h-6 w-6 animate-spin text-teal" />;
  const set = (path, value) => {
    const next = JSON.parse(JSON.stringify(doc));
    const parts = path.split(".");
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setDoc(next);
  };
  const save = async () => {
    setBusy(true);
    try { const { data } = await ax.put("/site-settings/_", doc); setDoc(data); toast.success("Site settings saved"); }
    catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };
  const Field = ({ label, path }) => (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">{label}</span>
      <input data-testid={`site-${path.replace(/\./g, '-')}`} className={inputCls} value={path.split(".").reduce((o, k) => o?.[k] ?? "", doc)} onChange={(e) => set(path, e.target.value)} />
    </label>
  );
  return (
    <>
      <h1 className="font-display font-bold text-3xl text-navy-800">Site Settings</h1>
      <div className="mt-8 grid gap-5 md:grid-cols-2 max-w-4xl">
        <Field label="Brand name" path="brand.name" />
        <Field label="Short name" path="brand.short_name" />
        <Field label="Tagline" path="brand.tagline" />
        <Field label="Tagline (dotted)" path="brand.tagline_dotted" />
        <Field label="Domain" path="domain" />
        <Field label="Canonical base" path="canonical_base" />
        <Field label="Contact email" path="contact.email" />
        <Field label="Contact phone" path="contact.phone" />
        <Field label="Address line 1" path="contact.address_line_1" />
        <Field label="City" path="contact.city" />
        <Field label="Country" path="contact.country" />
        <Field label="Map embed URL" path="contact.map_embed_url" />
      </div>
      <button data-testid="site-settings-save" onClick={save} disabled={busy} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors disabled:opacity-60">
        <Save className="h-4 w-4" /> Save changes
      </button>
    </>
  );
}

function InboxView() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    ax.get(`/contact-messages${filter ? `?source=${filter}` : ""}`).then(({ data }) => setList(data.items));
  }, [filter]);
  const mark = async (id, status) => {
    await ax.put(`/contact-messages/${id}`, { status });
    const { data } = await ax.get(`/contact-messages${filter ? `?source=${filter}` : ""}`);
    setList(data.items);
  };
  return (
    <>
      <h1 className="font-display font-bold text-3xl text-navy-800">Contact Inbox</h1>
      <div className="mt-6 flex gap-2">
        {["", "quote_request", "booking", "contact_form"].map(f => (
          <button key={f || "all"} data-testid={`inbox-filter-${f || "all"}`} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-semibold ${filter === f ? "bg-navy text-white" : "bg-white text-navy-700 border border-navy-100"}`}>
            {f || "All"}
          </button>
        ))}
      </div>
      <div className="mt-6 rounded-2xl bg-white border border-navy-50 divide-y divide-navy-50">
        {list.length === 0 ? <p className="p-10 text-center text-navy-700/70">No messages.</p> :
          list.map(m => (
            <div key={m.id} data-testid={`inbox-row-${m.id}`} className="p-5 grid gap-3 md:grid-cols-12">
              <div className="md:col-span-3">
                <p className="font-semibold text-navy-800">{m.name}</p>
                <p className="text-xs text-navy-500">{m.email}</p>
                <p className="text-xs text-navy-400 mt-1">{new Date(m.received_at).toLocaleString()}</p>
              </div>
              <div className="md:col-span-6">
                <p className="text-sm font-semibold text-navy-800">{m.subject}</p>
                <p className="mt-1 text-sm text-navy-700/80 line-clamp-2">{m.message}</p>
              </div>
              <div className="md:col-span-3 flex flex-col items-start gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal/10 text-teal text-[11px] font-semibold uppercase tracking-wider">{m.source || "form"}</span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-navy/10 text-navy text-[11px] font-semibold uppercase tracking-wider">{m.status || "new"}</span>
                <div className="flex gap-1">
                  <button onClick={() => mark(m.id, "read")} className="px-3 py-1 rounded-full bg-soft-gray text-xs font-semibold">Read</button>
                  <button onClick={() => mark(m.id, "replied")} className="px-3 py-1 rounded-full bg-soft-gray text-xs font-semibold">Replied</button>
                </div>
              </div>
            </div>
          ))}
      </div>
    </>
  );
}

function MediaLibrary() {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => ax.get("/media").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await ax.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-3xl text-navy-800">Media Library</h1>
        <label data-testid="media-upload" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal cursor-pointer">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
          <input type="file" accept="image/*,application/pdf" onChange={onUpload} className="hidden" />
        </label>
      </div>
      <div className="mt-8 grid gap-3 md:grid-cols-6">
        {list.map(m => (
          <div key={m.id} className="aspect-square rounded-2xl bg-white border border-navy-50 flex items-center justify-center overflow-hidden">
            {m.kind === "image" ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt={m.alt || m.filename} className="h-full w-full object-cover" />
            ) : (
              <div className="text-center p-3"><FileText className="h-8 w-8 text-navy mx-auto" /><p className="text-[10px] mt-2 truncate">{m.filename}</p></div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function AuditLogView() {
  const [list, setList] = useState([]);
  useEffect(() => { ax.get("/audit-log").then(({ data }) => setList(data)); }, []);
  return (
    <>
      <h1 className="font-display font-bold text-3xl text-navy-800">Audit Log</h1>
      <div className="mt-6 rounded-2xl bg-white border border-navy-50 divide-y divide-navy-50">
        {list.map(a => (
          <div key={a.id} className="p-4 grid grid-cols-12 gap-2 text-sm">
            <span className="col-span-3 text-navy-500">{new Date(a.at).toLocaleString()}</span>
            <span className="col-span-2 font-semibold text-navy-800">{a.action}</span>
            <span className="col-span-2 text-navy-700">{a.resource_type}</span>
            <span className="col-span-3 text-navy-500 truncate">{a.actor_email}</span>
            <span className="col-span-2 text-navy-400 truncate">{a.resource_id}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function PagesList() {
  const [list, setList] = useState([]);
  useEffect(() => { ax.get("/pages-list").then(({ data }) => setList(data)); }, []);
  return (
    <>
      <h1 className="font-display font-bold text-3xl text-navy-800">Pages</h1>
      <p className="mt-2 text-sm text-navy-700/70">{list.length} pages. Click to edit.</p>
      <div className="mt-6 rounded-2xl bg-white border border-navy-50 divide-y divide-navy-50">
        {list.map(p => {
          const slug = p._key || p.slug;
          return (
            <Link key={slug} to={`/admin/pages/${slug}`} data-testid={`pages-row-${slug}`} className="p-5 flex items-center justify-between hover:bg-soft-gray transition-colors">
              <div>
                <p className="font-semibold text-navy-800">{slug}</p>
                <p className="text-xs text-navy-500">{p.seo?.title || "(no SEO title)"}</p>
              </div>
              <span className="text-xs text-navy-400">{p.updated_at?.slice(0, 10) || ""}</span>
            </Link>
          );
        })}
      </div>
    </>
  );
}

// Shell wraps every authenticated page
function Shell({ children }) {
  return <AdminShell>{children}</AdminShell>;
}

const RESOURCES = [
  { slug: "divisions", label: "Divisions" },
  { slug: "services", label: "Services" },
  { slug: "team-members", label: "Team members" },
  { slug: "projects", label: "Projects" },
  { slug: "faqs", label: "FAQs" },
  { slug: "testimonials", label: "Testimonials" },
  { slug: "partners", label: "Partners" },
  { slug: "certifications", label: "Certifications" },
  { slug: "posts", label: "News / Posts" },
  { slug: "industries", label: "Industries" },
  { slug: "why-cards", label: "Why-choose cards" },
  { slug: "stats", label: "Stats" },
];

export default function AdminApp() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/admin" element={<ProtectedRoute><Shell><Dashboard /></Shell></ProtectedRoute>} />
          <Route path="/admin/site-settings" element={<ProtectedRoute><Shell><SiteSettingsEditor /></Shell></ProtectedRoute>} />
          <Route path="/admin/pages" element={<ProtectedRoute><Shell><PagesList /></Shell></ProtectedRoute>} />
          <Route path="/admin/pages/:slug" element={<ProtectedRoute><Shell><PageEditor /></Shell></ProtectedRoute>} />
          {RESOURCES.map(r => (
            <React.Fragment key={r.slug}>
              <Route path={`/admin/${r.slug}`} element={<ProtectedRoute><Shell><ResourceListWithEdit resource={r.slug} label={r.label} /></Shell></ProtectedRoute>} />
              <Route path={`/admin/${r.slug}/:key`} element={<ProtectedRoute><Shell><ResourceEditor resource={r.slug} /></Shell></ProtectedRoute>} />
            </React.Fragment>
          ))}
          <Route path="/admin/media" element={<ProtectedRoute><Shell><MediaLibrary /></Shell></ProtectedRoute>} />
          <Route path="/admin/inbox" element={<ProtectedRoute><Shell><InboxView /></Shell></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute><Shell><AuditLogView /></Shell></ProtectedRoute>} />
          <Route path="/admin/email-settings" element={<ProtectedRoute><Shell><EmailSettings /></Shell></ProtectedRoute>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
